/**
 * What the viewer may do with one task, derived from the permissions the server sent and the
 * task's assignment category. A reflection only: the server decides every request.
 */
import { can, PERMISSIONS } from '@/shared/lib/permissions';

export type TaskCategory = 'me' | 'others' | 'shared' | 'unassigned';

export interface AssignmentSource {
  assignment_kind: 'unassigned' | 'assigned' | 'shared';
  assignee_ids: readonly string[];
}

export function taskCategory(card: AssignmentSource, viewerId: string | null): TaskCategory {
  if (card.assignment_kind === 'shared') return 'shared';
  if (card.assignment_kind === 'assigned' && card.assignee_ids.length > 0) {
    return viewerId !== null && card.assignee_ids.includes(viewerId) ? 'me' : 'others';
  }
  return 'unassigned';
}

const EDIT: Record<TaskCategory, string> = {
  me: PERMISSIONS.taskEditAssignedToMe,
  others: PERMISSIONS.taskEditAssignedToOthers,
  shared: PERMISSIONS.taskEditShared,
  unassigned: PERMISSIONS.taskEditUnassigned,
};

export interface TaskAbilities {
  editContent: boolean;
  editSchedule: boolean;
  manageLabels: boolean;
  editAttachments: boolean;
  viewAttachments: boolean;
  viewHistory: boolean;
  restoreVersions: boolean;
  assign: boolean;
  comment: boolean;
  moderateComments: boolean;
  move: boolean;
  remove: boolean;
  watch: boolean;
}

export const ALL_TASK_ABILITIES: TaskAbilities = {
  editContent: true,
  editSchedule: true,
  manageLabels: true,
  editAttachments: true,
  viewAttachments: true,
  viewHistory: true,
  restoreVersions: true,
  assign: true,
  comment: true,
  moderateComments: true,
  move: true,
  remove: true,
  watch: true,
};

export function taskAbilities(
  permissions: readonly string[],
  card: AssignmentSource,
  viewerId: string | null,
): TaskAbilities {
  const category = taskCategory(card, viewerId);
  const editContent = can(permissions, EDIT[category]);
  return {
    editContent,
    // Dates and labels also need the right to edit this particular task.
    editSchedule: editContent && can(permissions, PERMISSIONS.taskEditSchedule),
    manageLabels: editContent && can(permissions, PERMISSIONS.taskManageLabels),
    editAttachments: editContent && can(permissions, PERMISSIONS.taskEditAttachments),
    viewAttachments: can(permissions, PERMISSIONS.taskViewAttachments),
    viewHistory: can(permissions, PERMISSIONS.taskViewHistory),
    restoreVersions: editContent && can(permissions, PERMISSIONS.taskRestoreVersions),
    assign: can(permissions, PERMISSIONS.taskAssign),
    comment: can(permissions, PERMISSIONS.taskComment),
    moderateComments: can(permissions, PERMISSIONS.taskModerateComments),
    move: can(permissions, PERMISSIONS.taskMove),
    remove: can(permissions, PERMISSIONS.taskDelete),
    watch: can(permissions, PERMISSIONS.taskWatch),
  };
}
